import { Injectable, inject } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { AratekDevice } from "./aratek.device";
import { ConnectedReader, FingerprintDevice, FingerprintDeviceError, FingerprintDevicePreference } from "./fingerprint-device";
import { MantraDevice } from "./mantra.device";
import { StartekDevice } from "./startek.device";

const STORAGE_KEY = "bas.fpDevice";

/**
 * Picks the fingerprint reader for this PC. Readers are physical, so the choice is kept per
 * browser in localStorage; FP_DEVICE in .env sets the default (AUTO when unset).
 */
@Injectable({ providedIn: "root" })
export class FingerprintDeviceService {
  private readonly startek = inject(StartekDevice);
  private readonly aratek = inject(AratekDevice);
  private readonly mantra = inject(MantraDevice);

  readonly options: { value: FingerprintDevicePreference; label: string }[] = [
    { value: "AUTO", label: "Auto detect" },
    { value: "STARTEK", label: this.startek.label },
    { value: "ARATEK", label: this.aratek.label },
    // { value: "MANTRA", label: this.mantra.label },
  ];

  get preference(): FingerprintDevicePreference {
    let stored: string | null = null;
    try {
      stored = localStorage.getItem(STORAGE_KEY);
    } catch {}
    return this.valid(stored) ?? this.valid(process.env.FP_DEVICE) ?? "AUTO";
  }

  set preference(value: FingerprintDevicePreference) {
    try {
      localStorage.setItem(STORAGE_KEY, value);
    } catch {}
  }

  connect(): Observable<ConnectedReader> {
    const preference = this.preference;
    if (preference !== "AUTO") {
      return this.device(preference).connect();
    }
    // Auto: Aratek, then Mantra, then Startek. Startek can't be probed without capturing, so
    // if its capture can't reach the service either, no reader was found at all. A service that
    // answered "no reader connected" is remembered, so that message wins over the generic one.
    let noDevice: FingerprintDeviceError | undefined;
    const orElse = (next: () => Observable<ConnectedReader>) =>
      catchError<ConnectedReader, Observable<ConnectedReader>>((error) => {
        if (!(error instanceof FingerprintDeviceError && (error.kind === "not-found" || error.kind === "no-device"))) {
          return throwError(() => error);
        }
        if (error.kind === "no-device") {
          noDevice ??= error;
        }
        return next();
      });

    return this.aratek.connect().pipe(
      orElse(() => this.mantra.connect()),
      orElse(() =>
        this.startek.connect().pipe(
          map((reader) => ({
            ...reader,
            unavailableError:
              noDevice ??
              new FingerprintDeviceError(
                "no-device",
                "No fingerprint reader found. Connect an Aratek or Startek FM220 reader to this PC and make sure " +
                  "its service is running (Aratek bridge or FM220 service), then try again."
              ),
          }))
        )
      )
    );
  }

  private device(id: Exclude<FingerprintDevicePreference, "AUTO">): FingerprintDevice {
    return { STARTEK: this.startek, ARATEK: this.aratek, MANTRA: this.mantra }[id];
  }

  private valid(value: string | null | undefined): FingerprintDevicePreference | null {
    const upper = (value || "").toUpperCase();
    return this.options.some((o) => o.value === upper) ? (upper as FingerprintDevicePreference) : null;
  }
}
